import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/authService";
import { dashboardService } from "../services/dashboardService";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Pie } from "react-chartjs-2";
import {
  CurrencyRupeeIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  BuildingOffice2Icon,
  ShoppingBagIcon,
  TruckIcon,
  SparklesIcon,
  GiftIcon,
  HandThumbUpIcon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ChevronUpDownIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  DocumentCurrencyRupeeIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const Dashboard = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize theme from localStorage, default to true (dark mode) if not found
    const savedTheme = localStorage.getItem("ownerapp-theme");
    return savedTheme ? JSON.parse(savedTheme) : false;
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().split("T")[0], // Today's date
    to: new Date().toISOString().split("T")[0], // Today's date
  });
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Comprehensive authentication error handler with redirection
  const handleAuthError = useCallback(
    (error, context = "Unknown") => {
      console.log(`🚨 Authentication error in ${context}:`, error);

      // Check if it's an authentication-related error
      const isAuthError =
        error?.response?.status === 401 ||
        error?.response?.status === 403 ||
        error?.message?.toLowerCase().includes("unauthorized") ||
        error?.message?.toLowerCase().includes("token") ||
        error?.message?.toLowerCase().includes("session");

      if (isAuthError) {
        console.log("🔑 Authentication error detected - redirecting to login");

        // Clear all authentication data
        authService.logout();

        // Set error message for user
        setError("Your session has expired. Redirecting to login...");

        // Redirect to login page after a short delay
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 2000);

        return true; // Indicates this was handled as an auth error
      }

      return false; // Not an auth error
    },
    [navigate]
  );

  // Keep-alive mechanism to prevent session expiry
  useEffect(() => {
    // Immediately keep token alive on component mount
    if (authService.isAuthenticated()) {
      authService.keepTokenAlive();
      console.log("🚀 Dashboard mounted - token immediately kept alive");
    }

    const keepAliveInterval = setInterval(async () => {
      try {
        // Make a lightweight API call every 2 minutes to keep session active
        // This is very aggressive to prevent any expiration
        await dashboardService.getSummary();

        // Keep token alive indefinitely - this prevents session expiration
        authService.keepTokenAlive();

        const tokenAge = authService.getTokenAge();
        console.log(
          `✅ Session keep-alive successful at ${new Date().toLocaleTimeString()} (Token age: ${tokenAge} mins)`
        );
      } catch (error) {
        console.log("❌ Keep-alive failed:", error.message);

        // Even on API failure, keep the token alive
        authService.keepTokenAlive();

        // Use comprehensive auth error handler
        const wasAuthError = handleAuthError(error, "Keep-alive");

        if (!wasAuthError) {
          // For non-auth errors, just log them
          console.log(
            "❌ Keep-alive failed with non-auth error:",
            error.message
          );
        }
      }
    }, 2 * 60 * 1000); // 2 minutes in milliseconds (very aggressive)

    // Also add user activity tracking to keep session alive on interaction
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    let lastActivity = Date.now();

    const handleUserActivity = async () => {
      const now = Date.now();
      // If it's been more than 30 seconds since last activity, make a keep-alive call
      if (now - lastActivity > 30000) {
        try {
          await dashboardService.getSummary();
          authService.keepTokenAlive();
          console.log("🎯 Activity-triggered keep-alive successful");
        } catch (error) {
          console.log(
            "🎯 Activity-triggered keep-alive failed:",
            error.message
          );
          // Even on failure, keep token alive
          authService.keepTokenAlive();
        }
      }
      lastActivity = now;
    };

    // Add activity listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Additional ultra-aggressive token persistence - runs every minute
    const tokenPersistenceInterval = setInterval(() => {
      if (authService.isAuthenticated()) {
        authService.keepTokenAlive();
        console.log("🛡️ Token persistence check - token kept alive");
      }
    }, 60 * 1000); // Every 1 minute

    // Cleanup interval and listeners on component unmount
    return () => {
      clearInterval(keepAliveInterval);
      clearInterval(tokenPersistenceInterval);
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [handleAuthError]);

  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("ownerapp-theme", JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const fetchSummaryData = useCallback(
    async (fromDate = null, toDate = null, isRetry = false) => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors when fetching new data

        if (!isRetry) {
          setRetryCount(0); // Reset retry count for new requests
        }

        const response = await dashboardService.getSummary();

        // Parse jsondata and filter by date range, then combine matching data
        let combinedData = {};
        let allInvoiceGroupSales = [];
        let allPMCollection = [];
        let allCapSale = [];
        let allTop20ItemSale = [];

        if (response.data && response.data.length > 0) {
          response.data.forEach((row, index) => {
            if (row.jsondata) {
              try {
                const parsedData = JSON.parse(row.jsondata);

                // Check if this row matches the selected date range using the root-level transactiondate
                let includeRow = true;
                if (fromDate && toDate) {
                  // Use the root-level transactiondate field instead of the empty one in jsondata
                  const transactionDateToUse =
                    row.transactiondate || parsedData.TransactionDate;

                  if (!transactionDateToUse || transactionDateToUse === "") {
                    includeRow = true;
                  } else {
                    // Use moment.js for better date parsing and comparison
                    const transactionDate = transactionDateToUse; // Use the correct date field

                    // Parse the ISO date format: "2025-09-06T00:00:00.000Z"
                    const rowMoment = moment(transactionDate);
                    const fromMoment = moment(fromDate);
                    const toMoment = moment(toDate);

                    // Compare only the date part (ignore time)
                    const rowDateOnly = rowMoment.format("YYYY-MM-DD");
                    const fromDateOnly = fromMoment.format("YYYY-MM-DD");
                    const toDateOnly = toMoment.format("YYYY-MM-DD");

                    includeRow =
                      rowDateOnly >= fromDateOnly && rowDateOnly <= toDateOnly;
                  }
                } else {
                  includeRow = true;
                }

                if (includeRow) {
                  // Combine numeric values from matching dates
                  Object.keys(parsedData).forEach((key) => {
                    if (
                      typeof parsedData[key] === "string" &&
                      !isNaN(parseFloat(parsedData[key]))
                    ) {
                      const numValue = parseFloat(parsedData[key]);
                      if (!isNaN(numValue)) {
                        const oldValue = combinedData[key] || 0;
                        combinedData[key] = oldValue + numValue;
                      }
                    } else if (typeof parsedData[key] === "number") {
                      const oldValue = combinedData[key] || 0;
                      combinedData[key] = oldValue + parsedData[key];
                    } else if (key === "StoreId" || key === "BranchId") {
                      combinedData[key] = parsedData[key];
                    }
                  });

                  // Combine arrays from matching dates
                  if (parsedData.RestInvoiceGroupSales) {
                    allInvoiceGroupSales = allInvoiceGroupSales.concat(
                      parsedData.RestInvoiceGroupSales
                    );
                  }
                  if (parsedData.RestPMCollection) {
                    allPMCollection = allPMCollection.concat(
                      parsedData.RestPMCollection
                    );
                  }
                  if (parsedData.RestCapSale) {
                    allCapSale = allCapSale.concat(parsedData.RestCapSale);
                  }
                  if (parsedData.RestTop20ItemSale) {
                    allTop20ItemSale = allTop20ItemSale.concat(
                      parsedData.RestTop20ItemSale
                    );
                  }
                }
              } catch (error) {
                // Silently handle JSON parsing errors
              }
            }
          });
        }

        // Process data - combine data from matching dates
        const processedData = {
          ...combinedData,
          RestInvoiceGroupSales: allInvoiceGroupSales,
          RestPMCollection: allPMCollection,
          RestCapSale: allCapSale,
          RestTop20ItemSale: allTop20ItemSale,
        };

        setSummaryData({
          ...response,
          processedData: processedData,
        });
        setError(null);
      } catch (err) {
        console.error("Dashboard fetch error:", err);

        // Use comprehensive authentication error handler first
        const wasAuthError = handleAuthError(err, "Dashboard fetch");

        if (!wasAuthError) {
          // Handle non-authentication errors
          if (err.response?.status >= 500) {
            const serverError =
              "Server is temporarily unavailable. Please try again in a few moments.";
            setError(serverError);

            // Auto-retry for server errors (max 2 times)
            if (retryCount < 2) {
              console.log(
                `Auto-retrying in 3 seconds... (attempt ${retryCount + 1}/2)`
              );
              setTimeout(() => {
                setRetryCount((prev) => prev + 1);
                fetchSummaryData(fromDate, toDate, true);
              }, 3000);
            }
          } else {
            setError(
              err.response?.data?.message || "Failed to fetch dashboard data"
            );
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [retryCount, handleAuthError]
  );

  useEffect(() => {
    // Load today's data by default
    const today = new Date().toISOString().split("T")[0];
    fetchSummaryData(today, today);
  }, [fetchSummaryData]);

  const handleLogout = () => {
    logout();
  };

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    fetchSummaryData(dateRange.from, dateRange.to);
    setShowDateFilter(false); // Hide date filter after search
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const aNum = parseFloat(aValue) || 0;
      const bNum = parseFloat(bValue) || 0;

      return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
    });
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Quick select helper functions
  const handleQuickSelect = (period) => {
    const today = moment();
    let from, to;

    switch (period) {
      case "today":
        from = to = today.format("YYYY-MM-DD");
        break;
      case "yesterday":
        from = to = today.subtract(1, "day").format("YYYY-MM-DD");
        break;
      case "last7days":
        from = today.subtract(6, "days").format("YYYY-MM-DD");
        to = moment().format("YYYY-MM-DD");
        break;
      case "last30days":
        from = today.subtract(29, "days").format("YYYY-MM-DD");
        to = moment().format("YYYY-MM-DD");
        break;
      case "thismonth":
        from = moment().startOf("month").format("YYYY-MM-DD");
        to = moment().endOf("month").format("YYYY-MM-DD");
        break;
      case "lastmonth":
        from = moment()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD");
        to = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
        break;
      default:
        return;
    }

    setDateRange({ from, to });
    fetchSummaryData(from, to);
    setShowDateFilter(false);
  };

  // Chart.js data preparation functions
  const getCollectionChartData = () => {
    if (!summaryData?.processedData?.RestPMCollection) return null;

    const aggregatedPayments = {};
    summaryData.processedData.RestPMCollection.forEach((payment) => {
      const mode = payment.PaymentMode;
      if (aggregatedPayments[mode]) {
        aggregatedPayments[mode] += parseFloat(payment.TotalCollection);
      } else {
        aggregatedPayments[mode] = parseFloat(payment.TotalCollection);
      }
    });

    const labels = Object.keys(aggregatedPayments);
    const data = Object.values(aggregatedPayments);

    // Check if all values are zero
    const hasNonZeroData = data.some((value) => value > 0);
    if (!hasNonZeroData) return null;

    return {
      labels,
      datasets: [
        {
          label: "Collection Amount (₹)",
          data,
          backgroundColor: [
            "#3b82f6",
            "#60a5fa",
            "#f59e0b",
            "#ef4444",
            "#10b981",
            "#8b5cf6",
          ],
          borderColor: isDarkMode ? "#374151" : "#ffffff",
          borderWidth: 2,
        },
      ],
    };
  };

  const getCaptainChartData = () => {
    if (!summaryData?.processedData?.RestCapSale) return null;

    const aggregatedCaptains = {};
    summaryData.processedData.RestCapSale.forEach((captain) => {
      const name = captain.CaptainName;
      if (aggregatedCaptains[name]) {
        aggregatedCaptains[name] += parseFloat(captain.TotalSale);
      } else {
        aggregatedCaptains[name] = parseFloat(captain.TotalSale);
      }
    });

    const labels = Object.keys(aggregatedCaptains);
    const data = Object.values(aggregatedCaptains);

    // Check if all values are zero
    const hasNonZeroData = data.some((value) => value > 0);
    if (!hasNonZeroData) return null;

    return {
      labels,
      datasets: [
        {
          label: "Sales Amount (₹)",
          data,
          backgroundColor: [
            "#3b82f6",
            "#60a5fa",
            "#f59e0b",
            "#ef4444",
            "#10b981",
            "#8b5cf6",
          ],
          borderColor: isDarkMode ? "#374151" : "#ffffff",
          borderWidth: 2,
        },
      ],
    };
  };

  const getSalesSummaryPieData = () => {
    if (!summaryData?.processedData?.RestInvoiceGroupSales) return null;

    const invoiceGroupSales = summaryData.processedData.RestInvoiceGroupSales;

    // Check if data exists and has valid entries
    if (!Array.isArray(invoiceGroupSales) || invoiceGroupSales.length === 0)
      return null;

    // Aggregate data by InvoiceGroupName
    const groupedData = {};

    invoiceGroupSales.forEach((item) => {
      const groupName = item.InvoiceGroupName || "Unknown";
      const saleAmount = parseFloat(item.TotalSale || 0);

      if (saleAmount > 0) {
        if (groupedData[groupName]) {
          groupedData[groupName] += saleAmount;
        } else {
          groupedData[groupName] = saleAmount;
        }
      }
    });

    // Convert to arrays for chart
    const labels = Object.keys(groupedData);
    const data = Object.values(groupedData);

    // Check if we have any valid data
    if (labels.length === 0) return null;

    const backgroundColors = [
      "#14b8a6", // teal - FOOD
      "#f59e0b", // amber - BAR
      "#ef4444", // red
      "#3b82f6", // blue
      "#8b5cf6", // violet
      "#f97316", // orange
      "#06b6d4", // cyan
      "#84cc16", // lime
      "#ec4899", // pink
      "#6b7280", // gray
    ];

    return {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: backgroundColors.slice(0, labels.length),
          borderColor: isDarkMode ? "#374151" : "#ffffff",
          borderWidth: 2,
        },
      ],
    };
  };

  const getTaxSummaryPieData = () => {
    if (!summaryData?.processedData) return null;

    const sgst = parseFloat(summaryData.processedData.TotalSGST || 0);
    const cgst = parseFloat(summaryData.processedData.TotalCGST || 0);
    const scharge = parseFloat(summaryData.processedData.TotalSCharge || 0);
    const vat = parseFloat(summaryData.processedData.VATAmount || 0);

    // Check if all tax values are zero
    if (sgst === 0 && cgst === 0 && scharge === 0 && vat === 0) return null;

    return {
      labels: ["SGST", "CGST", "SCharge", "VAT"],
      datasets: [
        {
          data: [sgst, cgst, scharge, vat],
          backgroundColor: ["#eab308", "#14b8a6", "#f59e0b", "#ef4444"],
          borderColor: isDarkMode ? "#374151" : "#ffffff",
          borderWidth: 2,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30, // Add padding at the top to show values above bars
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: isDarkMode ? "#d1d5db" : "#374151",
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
        titleColor: isDarkMode ? "#ffffff" : "#000000",
        bodyColor: isDarkMode ? "#d1d5db" : "#374151",
        borderColor: isDarkMode ? "#374151" : "#e5e7eb",
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            // For bar charts, use context.parsed.y
            const value = context.parsed.y;
            return `${context.label}: ₹${value.toFixed(2)}`;
          },
        },
      },
      datalabels: {
        anchor: "end",
        align: "top",
        color: isDarkMode ? "#ffffff" : "#374151",
        font: {
          weight: "bold",
          size: 10,
        },
        formatter: function (value, context) {
          if (value > 0) {
            return "₹" + value.toFixed(0);
          }
          return "";
        },
        display: function (context) {
          // Check if context.parsed exists and has the expected structure
          if (
            context &&
            context.parsed &&
            typeof context.parsed.y === "number"
          ) {
            return context.parsed.y > 0;
          }
          // Fallback: check if value is available directly
          if (
            context &&
            context.dataset &&
            context.dataset.data &&
            context.dataIndex !== undefined
          ) {
            const value = context.dataset.data[context.dataIndex];
            return value > 0;
          }
          return false;
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: isDarkMode ? "#d1d5db" : "#374151",
          callback: function (value) {
            return "₹" + value;
          },
        },
        grid: {
          color: isDarkMode ? "#374151" : "#e5e7eb",
        },
        title: {
          display: true,
          text: "Amount (₹)",
          color: isDarkMode ? "#d1d5db" : "#374151",
        },
      },
      x: {
        ticks: {
          color: isDarkMode ? "#d1d5db" : "#374151",
        },
        grid: {
          color: isDarkMode ? "#374151" : "#e5e7eb",
        },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: isDarkMode ? "#d1d5db" : "#374151",
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
        titleColor: isDarkMode ? "#ffffff" : "#000000",
        bodyColor: isDarkMode ? "#d1d5db" : "#374151",
        borderColor: isDarkMode ? "#374151" : "#e5e7eb",
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            // For pie charts, use context.parsed directly
            const value = context.parsed;
            return `${context.label}: ₹${value.toFixed(2)}`;
          },
        },
      },
      datalabels: {
        color: "#ffffff", // Always use white text for better contrast
        font: {
          weight: "bold",
          size: 11,
        },
        formatter: function (value, context) {
          if (!context || !context.dataset || !context.dataset.data || !value) {
            return "";
          }
          // Calculate percentage
          const total = context.dataset.data.reduce(
            (acc, curr) => acc + (curr || 0),
            0
          );
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

          // Show percentage and value for segments larger than 5%
          if (percentage > 8) {
            return percentage + "%\n₹" + value.toFixed(0);
          } else if (percentage > 5) {
            return percentage + "%";
          }
          return ""; // Hide label for small segments
        },
        align: "center",
        anchor: "center",
        textAlign: "center",
        borderColor: "#ffffff",
        borderRadius: 3,
        borderWidth: 1,
        backgroundColor: function (context) {
          // Semi-transparent black background for better readability
          return "rgba(0, 0, 0, 0.7)";
        },
        padding: 2,
        display: function (context) {
          // Only display labels for segments larger than 5%
          if (!context || !context.dataset || !context.dataset.data) {
            return false;
          }

          const total = context.dataset.data.reduce(
            (acc, curr) => acc + curr,
            0
          );

          // Get the value - try different approaches
          let value = 0;
          if (context.parsed !== undefined && context.parsed !== null) {
            value = context.parsed;
          } else if (
            context.dataIndex !== undefined &&
            context.dataset.data[context.dataIndex] !== undefined
          ) {
            value = context.dataset.data[context.dataIndex];
          }

          const percentage = total > 0 ? (value / total) * 100 : 0;
          return percentage > 5;
        },
      },
    },
  };

  return (
    <div
      className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } shadow-sm`}
      >
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-3">
              <img
                src="/logo.png"
                alt="Owner Dashboard Logo"
                className="h-10 w-10 shadow-sm"
              />
              <h1
                className={`text-sm font-semibold text-left ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
                style={{ lineHeight: 1.2 }}
              >
                Owner
                <br />
                Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-md ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white hover:bg-gray-700"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
                title={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
              >
                {isDarkMode ? (
                  <SunIcon className="h-5 w-5" />
                ) : (
                  <MoonIcon className="h-5 w-5" />
                )}
              </button>
              <button
                onClick={handleLogout}
                className={`p-2 rounded-md ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white hover:bg-gray-700"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-full mx-auto p-6 pt-20">
        <div>
          {/* Header with Welcome Message and Date Toggle */}
          <div className="rounded-lg mb-4">
            <div className="flex flex-col">
              {/* Date Display and Quick Actions */}
              <div className="flex items-center justify-between gap-6">
                <div className="ml-auto flex items-end gap-2 flex-col w-full">
                  <div className="flex items-center justify-between w-full">
                    {/* Name Display */}
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-blue-300" : "text-blue-700"
                      }`}
                    >
                      {user
                        ? `Hi, ${user.fullName || user.username || "User"}`
                        : "Welcome"}
                    </span>
                    {/* Date Display */}
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-blue-300" : "text-blue-700"
                      }`}
                    >
                      {dateRange.from === dateRange.to
                        ? `${moment(dateRange.from).format("DD-MM-YYYY")}`
                        : `${moment(dateRange.from).format(
                            "DD-MM-YYYY"
                          )}  to  ${moment(dateRange.to).format("DD-MM-YYYY")}`}
                    </span>
                  </div>

                  {/* Quick Action Buttons */}
                  <div className="flex items-center gap-1 justify-between w-full">
                    <button
                      onClick={() => handleQuickSelect("today")}
                      className={`px-3 py-2 text-xs font-medium rounded transition-colors duration-200 w-full ${
                        isDarkMode
                          ? "bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => handleQuickSelect("yesterday")}
                      className={`px-3 py-2 text-xs font-medium rounded transition-colors duration-200 w-full ${
                        isDarkMode
                          ? "bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Yesterday
                    </button>
                    <button
                      onClick={() => handleQuickSelect("last7days")}
                      className={`px-3 py-2 text-xs font-medium rounded transition-colors duration-200 w-full ${
                        isDarkMode
                          ? "bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Week
                    </button>
                    <button
                      onClick={() => setShowDateFilter(!showDateFilter)}
                      className={`px-3 py-2 text-xs font-medium rounded transition-colors duration-200  ${
                        isDarkMode
                          ? "bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <EllipsisVerticalIcon className="h-4 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* More Options Dropdown - Date Range Filter and Additional Quick Selects */}
              {showDateFilter && (
                <div
                  className={`mt-4 p-4 rounded-lg border ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-600"
                      : "bg-gray-50 border-gray-300"
                  }`}
                >
                  <div className="flex flex-col gap-3">
                    {/* Additional Quick Select Buttons */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[
                        { key: "last30days", label: "Last 30 days" },
                        { key: "thismonth", label: "This month" },
                        { key: "lastmonth", label: "Last month" },
                      ].map((period) => (
                        <button
                          key={period.key}
                          onClick={() => handleQuickSelect(period.key)}
                          className={`px-1 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                            isDarkMode
                              ? "bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white border border-gray-600"
                              : "bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 border border-gray-300"
                          }`}
                        >
                          {period.label}
                        </button>
                      ))}
                    </div>

                    {/* Custom Date Range Section */}
                    <div
                      className={`pt-3 border-t ${
                        isDarkMode ? "border-gray-600" : "border-gray-300"
                      }`}
                    >
                      <h4
                        className={`text-sm font-medium mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Custom Date Range
                      </h4>

                      {/* Date Inputs */}
                      <div className="flex items-center gap-2 mb-2">
                        <label
                          htmlFor="mobile-from-date"
                          style={{ width: "40px" }}
                          className={`text-sm font-medium whitespace-nowrap text-left ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          From
                        </label>
                        <input
                          type="date"
                          id="mobile-from-date"
                          className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                          value={dateRange.from}
                          onChange={(e) =>
                            handleDateChange("from", e.target.value)
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="mobile-to-date"
                          style={{ width: "40px" }}
                          className={`text-sm font-medium whitespace-nowrap text-left ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          To
                        </label>
                        <input
                          type="date"
                          id="mobile-to-date"
                          className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                          value={dateRange.to}
                          onChange={(e) =>
                            handleDateChange("to", e.target.value)
                          }
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={handleSearch}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md text-sm font-medium transition-colors duration-200"
                        >
                          Show Data
                        </button>
                        {/* <button
                        onClick={() => {
                          fetchSummaryData(null, null);
                          setShowDateFilter(false);
                        }}
                        className={`flex-1 px-6 py-3 rounded-md text-sm font-medium transition-colors duration-200 ${
                          isDarkMode
                            ? "bg-gray-600 hover:bg-gray-500 text-white"
                            : "bg-gray-600 hover:bg-gray-700 text-white"
                        }`}
                      >
                        Show All
                      </button> */}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="overflow-hidden shadow rounded-lg mb-6 h-full">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">
                    Loading dashboard data...
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Error loading dashboard
                    </h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                    {/* Show appropriate action buttons based on error type */}
                    <div className="mt-3 flex gap-3">
                      {typeof error === "string" &&
                        (/unauthorized|token|session|expired/i.test(error) ? (
                          <button
                            onClick={handleLogout}
                            className="text-sm bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors duration-200"
                          >
                            Login again
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                fetchSummaryData(dateRange.from, dateRange.to)
                              }
                              className="text-sm bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors duration-200"
                              disabled={loading}
                            >
                              {loading ? "Retrying..." : "Try again"}
                            </button>
                            {/* Add a refresh page option for server errors */}
                            {typeof error === "string" &&
                              /server|unavailable/i.test(error) && (
                                <button
                                  onClick={() => window.location.reload()}
                                  className="text-sm border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 rounded-md transition-colors duration-200"
                                >
                                  Refresh page
                                </button>
                              )}
                          </>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Initial State - No Data Loaded */}
          {!loading && !error && !summaryData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-blue-800">
                    Welcome to Your Dashboard
                  </h3>
                  <p className="mt-2 text-blue-600">
                    Select a date range and click "Q Search" to view your
                    business data, or click "Show All Data" to see everything.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Row 1 - Business Summary Cards (7 cards) */}
          {!loading && !error && summaryData && summaryData.processedData && (
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 mb-6">
              {/* 1. Total Collection */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Total Collection
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalCollection || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <CurrencyRupeeIcon className="text-green-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 2. Total Sales */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Total Sales
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalBilledAmount || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <ChartBarIcon className="text-blue-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 3. Total Unbilled */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Total Unbilled
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.UnbilledAmount || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <ClipboardDocumentListIcon className="text-yellow-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 4. Total Purchase */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Total Purchase
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.PurchaseTotal || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <ShoppingCartIcon className="text-orange-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 5. Total Expense */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Total Expense
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalExpense || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <CreditCardIcon className="text-red-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 6. Invoices */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Invoices
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {summaryData.processedData.TotalInvoices || "0"}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <DocumentTextIcon className="text-purple-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 7. AOV (Average Order Value) */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      AOV
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {summaryData.processedData.TotalInvoices > 0
                            ? parseFloat(
                                (summaryData.processedData.TotalBilledAmount ||
                                  0) / summaryData.processedData.TotalInvoices
                              ).toFixed(2)
                            : "0.00"}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <ArrowTrendingUpIcon className="text-indigo-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 8. Dine In Sales */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Dine In Sales
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalTableSale || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <BuildingOffice2Icon className="text-teal-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 9. Take Away Sales */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Take Away Sales
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalCounterSale || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <ShoppingBagIcon className="text-indigo-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 10. Parcel Charges */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Parcel Charges
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalParcelCharges || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <TruckIcon className="text-red-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 11. NC Sales */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      NC Sales
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalNCCollection || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <SparklesIcon className="text-cyan-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 12. Total Discount */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Total Discount
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalDiscount || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <GiftIcon className="text-amber-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 13. Total Tip */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Total Tip
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalTip || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <HandThumbUpIcon className="text-emerald-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* 14. Total Waive Off */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden shadow rounded-lg`}
              >
                <div className="p-5">
                  <div className="flex flex-col items-start gap-2">
                    <div
                      className={`text-xs font-medium truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Total Waive Off
                    </div>
                    <div className="flex items-center w-full">
                      <div className="mr-5 w-0 flex-1 text-left">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          ₹
                          {parseFloat(
                            summaryData.processedData.TotalWaiveOff || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <XMarkIcon className="text-slate-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Charts Section */}
          {!loading && !error && summaryData && summaryData.processedData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Sales Summary Pie Chart */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } shadow rounded-lg`}
              >
                <div className="px-4 py-5 sm:p-6">
                  <h3
                    className={`text-lg font-medium mb-4 text-left ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Sales Summary
                  </h3>
                  <div className="h-64">
                    {(() => {
                      const salesData = getSalesSummaryPieData();
                      return salesData ? (
                        <Pie
                          data={salesData}
                          options={pieOptions}
                          plugins={[ChartDataLabels]}
                        />
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center">
                          <div
                            className={`text-6xl mb-4 ${
                              isDarkMode ? "text-gray-600" : "text-gray-300"
                            }`}
                          >
                            <CurrencyRupeeIcon className="h-16 w-16" />
                          </div>
                          <p
                            className={`text-lg font-medium mb-2 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            No Sales Data
                          </p>
                          <p
                            className={`text-sm text-center ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Sales summary will appear here when data is
                            available
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Collection Summary Bar Chart */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } shadow rounded-lg`}
              >
                <div className="px-4 py-5 sm:p-6">
                  <h3
                    className={`text-lg font-medium mb-4 text-left ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Collection Summary
                  </h3>
                  <div className="h-64">
                    {(() => {
                      const collectionData = getCollectionChartData();
                      return collectionData ? (
                        <Bar
                          data={collectionData}
                          options={chartOptions}
                          plugins={[ChartDataLabels]}
                        />
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center">
                          <div
                            className={`text-6xl mb-4 ${
                              isDarkMode ? "text-gray-600" : "text-gray-300"
                            }`}
                          >
                            <CurrencyRupeeIcon className="h-16 w-16" />
                          </div>
                          <p
                            className={`text-lg font-medium mb-2 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            No Collection Data
                          </p>
                          <p
                            className={`text-sm text-center ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Collection summary will appear here when data is
                            available
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Tax Summary Pie Chart */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } shadow rounded-lg`}
              >
                <div className="px-4 py-5 sm:p-6">
                  <h3
                    className={`text-lg font-medium mb-4 text-left ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Tax Summary
                  </h3>
                  <div className="h-64">
                    {(() => {
                      const taxData = getTaxSummaryPieData();
                      return taxData ? (
                        <Pie
                          data={taxData}
                          options={pieOptions}
                          plugins={[ChartDataLabels]}
                        />
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center">
                          <div
                            className={`text-6xl mb-4 ${
                              isDarkMode ? "text-gray-600" : "text-gray-300"
                            }`}
                          >
                            <DocumentCurrencyRupeeIcon className="h-16 w-16" />
                          </div>
                          <p
                            className={`text-lg font-medium mb-2 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            No Tax Data
                          </p>
                          <p
                            className={`text-sm text-center ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Tax summary (SGST/CGST) will appear here when data
                            is available
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Captain Wise Sale Bar Chart */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } shadow rounded-lg`}
              >
                <div className="px-4 py-5 sm:p-6">
                  <h3
                    className={`text-lg font-medium mb-4 text-left ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Captain Wise Sale
                  </h3>
                  <div className="h-64">
                    {(() => {
                      const captainData = getCaptainChartData();
                      return captainData ? (
                        <Bar
                          data={captainData}
                          options={chartOptions}
                          plugins={[ChartDataLabels]}
                        />
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center">
                          <div
                            className={`text-6xl mb-4 ${
                              isDarkMode ? "text-gray-600" : "text-gray-300"
                            }`}
                          >
                            <UserGroupIcon className="h-16 w-16" />
                          </div>
                          <p
                            className={`text-lg font-medium mb-2 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            No Captain Data
                          </p>
                          <p
                            className={`text-sm text-center ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Captain-wise sales performance will appear here when
                            data is available
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Top Selling Items */}
          {!loading &&
            !error &&
            summaryData &&
            summaryData.processedData &&
            summaryData.processedData.RestTop20ItemSale && (
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } shadow rounded-lg mb-6`}
              >
                <div className="px-4 py-5 sm:p-6">
                  <h3
                    className={`text-lg font-medium mb-4 text-left ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Top Selling Items
                  </h3>
                  <div className="overflow-x-auto">
                    {summaryData.processedData.RestTop20ItemSale &&
                    summaryData.processedData.RestTop20ItemSale.length > 0 ? (
                      <table
                        className={`min-w-full divide-y ${
                          isDarkMode ? "divide-gray-700" : "divide-gray-200"
                        }`}
                      >
                        <thead
                          className={isDarkMode ? "bg-gray-700" : "bg-gray-50"}
                        >
                          <tr>
                            <th
                              className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-75 ${
                                isDarkMode
                                  ? "text-gray-300 hover:bg-gray-600"
                                  : "text-gray-500 hover:bg-gray-100"
                              }`}
                              onClick={() => handleSort("ItemName")}
                            >
                              <div className="flex items-center space-x-1">
                                <span>Item Name</span>
                                <ChevronUpDownIcon className="w-4 h-4" />
                              </div>
                            </th>
                            <th
                              className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-75 ${
                                isDarkMode
                                  ? "text-gray-300 hover:bg-gray-600"
                                  : "text-gray-500 hover:bg-gray-100"
                              }`}
                              onClick={() => handleSort("TotalQuantity")}
                            >
                              <div className="flex items-center justify-end space-x-1">
                                <span>Quantity</span>
                                <ChevronUpDownIcon className="w-4 h-4" />
                              </div>
                            </th>
                            <th
                              className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-75 ${
                                isDarkMode
                                  ? "text-gray-300 hover:bg-gray-600"
                                  : "text-gray-500 hover:bg-gray-100"
                              }`}
                              onClick={() => handleSort("ItemGroupName")}
                            >
                              <div className="flex items-center space-x-1">
                                <span>Category</span>
                                <ChevronUpDownIcon className="w-4 h-4" />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody
                          className={`${
                            isDarkMode
                              ? "bg-gray-800 divide-gray-700"
                              : "bg-white divide-gray-200"
                          } divide-y`}
                        >
                          {getSortedData(
                            summaryData.processedData.RestTop20ItemSale
                          )
                            .slice(0, 10)
                            .map((item, index) => (
                              <tr
                                key={index}
                                className={
                                  isDarkMode
                                    ? "hover:bg-gray-700"
                                    : "hover:bg-gray-50"
                                }
                              >
                                <td
                                  className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-left ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {item.ItemName}
                                </td>
                                <td
                                  className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {parseFloat(item.TotalQuantity).toFixed(1)}
                                </td>
                                <td
                                  className={`px-6 py-4 whitespace-nowrap text-sm text-left ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {item.ItemGroupName}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="h-64 flex flex-col items-center justify-center">
                        <div
                          className={`text-6xl mb-4 ${
                            isDarkMode ? "text-gray-600" : "text-gray-300"
                          }`}
                        >
                          <ShoppingCartIcon className="h-16 w-16" />
                        </div>
                        <p
                          className={`text-lg font-medium mb-2 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          No Items Data
                        </p>
                        <p
                          className={`text-sm text-center ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          Top selling items will appear here when sales data is
                          available
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
